// Insights-tab card group: surfaces correlations between WEARABLE biometrics
// (skin temp, HRV, sleep efficiency, resting HR) and outcomes (hot flashes /
// Menopause Impact Score). Sits ALONGSIDE TriggerInsightsCard and uses the same
// effect-size + confidence methodology (see triggerCorrelations.ts).
// Renders nothing when the user has no wearable_metrics rows.
import { useEffect, useState } from "react";
import {
  fetchWearableMetrics,
  correlateSkinTempHotFlash,
  type WearableMetricRow,
} from "./wearableMetrics";
import { fetchMenoScoreHistory } from "./menoScore";
import { fetchHotFlashEventsBetween, localDateKey, parseLocalDateKey } from "./dailyLogs";
import { supabase } from "@/integrations/supabase/client";

// --- shared tokens (mirror TriggerInsightsCard.tsx) ---
const TL = "#FFE4EA";
const TD = "#4B1F4F";
const GD = "#6E4B71";
const GR = "#6E4B71";
const GL = "#FAF7F5";
const WH = "#ffffff";
const RO = "#FF5C7A";
const TEAL = "#1D9E75";
const TEAL_L = "#D8F1E6";
const AMBER = "#D9A441";
const AMBER_L = "#FBEFD3";

type ConfidenceLevel = "low" | "building" | "confident";
type Direction = "helpful" | "harmful" | "mixed" | "unclear";

type BiometricInsight = {
  key: string;
  emoji: string;
  label: string;          // metric label
  outcomeLabel: string;   // outcome label
  effectSize: number;     // signed
  effectUnit: string;     // "score" / "hot flashes" / etc
  effectDescription: string;
  daysAnalyzed: number;
  confidence: ConfidenceLevel;
  direction: Direction;
  action: string;
};

// Same tiering function as the trigger engine.
function confidenceFor(days: number): ConfidenceLevel {
  if (days < 7) return "low";
  if (days < 21) return "building";
  return "confident";
}

const SCORE_UNCLEAR = 1;     // same as UNCLEAR_THRESHOLDS.meno_score
const HF_UNCLEAR = 0.5;      // same as UNCLEAR_THRESHOLDS.hot_flash_frequency

function mean(xs: number[]): number {
  if (!xs.length) return 0;
  return xs.reduce((a, b) => a + b, 0) / xs.length;
}

// Bucket each day by whether the metric was at/above vs below the user's
// rolling 14-day baseline (computed from prior days only). `polarity`:
//   "higher-better" → above = "high" bucket, below = "low"
//   "lower-better"  → below = "high" bucket (good side), above = "low"
// Returns the high-bucket score mean − low-bucket score mean (signed so that
// a positive value means the "high" bucket scored higher on Menopause Impact Score
// — which is the "better" side under either polarity).
function bucketAgainstBaseline(
  rows: WearableMetricRow[],
  field: keyof WearableMetricRow,
  scoreByDate: Record<string, number>,
  polarity: "higher-better" | "lower-better",
  baselineNights = 14,
): { high: number[]; low: number[] } {
  const out = { high: [] as number[], low: [] as number[] };
  // Sort by date ascending (fetchWearableMetrics already does, but defensive).
  const sorted = [...rows].sort((a, b) => a.metric_date.localeCompare(b.metric_date));
  for (let i = 0; i < sorted.length; i++) {
    const row = sorted[i];
    const v = row[field];
    if (typeof v !== "number" || !Number.isFinite(v)) continue;
    const score = scoreByDate[row.metric_date];
    if (typeof score !== "number") continue;
    // Rolling prior-N baseline (prior days only).
    const window: number[] = [];
    for (let j = Math.max(0, i - baselineNights); j < i; j++) {
      const p = sorted[j][field];
      if (typeof p === "number" && Number.isFinite(p)) window.push(p);
    }
    if (window.length < 3) continue; // need a baseline
    const base = mean(window);
    const above = v >= base;
    const isHighBucket = polarity === "higher-better" ? above : !above;
    if (isHighBucket) out.high.push(score);
    else out.low.push(score);
  }
  return out;
}

function buildScoreInsight(
  key: string,
  emoji: string,
  label: string,
  buckets: { high: number[]; low: number[] },
): BiometricInsight | null {
  if (buckets.high.length < 2 || buckets.low.length < 2) return null;
  const effect = mean(buckets.high) - mean(buckets.low); // + means score better in "good" bucket
  const days = buckets.high.length + buckets.low.length;
  const abs = Math.abs(effect);
  const confidence = confidenceFor(days);
  let direction: Direction;
  let effectDescription: string;
  let action: string;
  if (abs < SCORE_UNCLEAR) {
    direction = "unclear";
    effectDescription = `No clear Menopause Impact Score difference linked to your ${label.toLowerCase()} yet.`;
    action = "Watch how this shifts as more wearable nights come in.";
  } else if (effect > 0) {
    direction = "helpful";
    effectDescription = `Your Menopause Impact Score runs ~${Math.round(abs)} points higher on days when your ${label.toLowerCase()} is on the better side of your own baseline.`;
    action = "Keep doing what's working.";
  } else {
    direction = "harmful";
    effectDescription = `Your Menopause Impact Score runs ~${Math.round(abs)} points lower on days when your ${label.toLowerCase()} is on the off side of your own baseline.`;
    action = "Worth noticing on the rough days.";
  }
  return {
    key,
    emoji,
    label,
    outcomeLabel: "Menopause Impact Score",
    effectSize: Number(effect.toFixed(2)),
    effectUnit: "score",
    effectDescription,
    daysAnalyzed: days,
    confidence,
    direction,
    action,
  };
}

function ConfidenceDot({ level, days }: { level: ConfidenceLevel; days: number }) {
  const map = {
    low: { fg: AMBER, text: `Low confidence — ${days} day${days === 1 ? "" : "s"} of paired data` },
    building: { fg: RO, text: `Building confidence — ${days} day${days === 1 ? "" : "s"} of paired data` },
    confident: { fg: TEAL, text: `Confident — ${days} days of paired data` },
  } as const;
  const s = map[level];
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: GR }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: s.fg, flexShrink: 0 }} />
      <span style={{ color: s.fg, fontWeight: 500 }}>{s.text}</span>
    </div>
  );
}

function EffectBadge({ insight }: { insight: BiometricInsight }) {
  if (insight.direction === "unclear") {
    return (
      <span style={{ fontSize: 11, fontWeight: 500, color: GR, background: GL, borderRadius: 10, padding: "4px 10px" }}>
        Effect not clear yet
      </span>
    );
  }
  const abs = Math.abs(insight.effectSize);
  const arrow = insight.effectSize > 0 ? "↑" : "↓";
  const num = insight.effectUnit === "score" ? Math.round(abs).toString() : abs.toFixed(1);
  const helpful = insight.direction === "helpful";
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: helpful ? RO : TD,
        background: helpful ? TL : "#FDECEF",
        borderRadius: 10,
        padding: "4px 10px",
        whiteSpace: "nowrap",
      }}
    >
      {arrow} {num} {insight.effectUnit}
    </span>
  );
}

function Card({ insight }: { insight: BiometricInsight }) {
  return (
    <div
      style={{
        background: WH,
        border: "0.5px solid #E8D5DA",
        borderRadius: 18,
        padding: "16px 18px",
        animation: "bic-fade 320ms ease both",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span style={{ fontSize: 18 }}>{insight.emoji}</span>
          <span style={{ fontSize: 14, fontWeight: 500, color: TD }}>{insight.label}</span>
          <span style={{ fontSize: 11, color: GR }}>· {insight.outcomeLabel}</span>
        </div>
        <EffectBadge insight={insight} />
      </div>
      <div style={{ fontSize: 13, color: GD, lineHeight: 1.55, marginBottom: 10 }}>
        {insight.effectDescription}
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <ConfidenceDot level={insight.confidence} days={insight.daysAnalyzed} />
        <div style={{ fontSize: 11, color: GR, fontStyle: "italic" }}>{insight.action}</div>
      </div>
    </div>
  );
}

export function BiometricInsightsCard({ userId }: { userId: string }) {
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState<BiometricInsight[]>([]);
  const [hasWearable, setHasWearable] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Pull ~44 nights so we have a 14-night baseline + ~30 nights of pairing.
        const rows = await fetchWearableMetrics(userId, 44);
        if (cancelled) return;
        if (!rows.length) {
          setHasWearable(false);
          setInsights([]);
          setLoading(false);
          return;
        }
        setHasWearable(true);

        // Score history keyed by date (last 30 days; matches default trigger engine window).
        const scoreSeries = await fetchMenoScoreHistory(userId, 30);
        const scoreByDate: Record<string, number> = {};
        for (const s of scoreSeries) if (s.score != null) scoreByDate[s.dateKey] = s.score;

        // Hot-flash counts per date for the temp card.
        const todayKey = localDateKey();
        const today = parseLocalDateKey(todayKey)!;
        const startD = new Date(today); startD.setDate(today.getDate() - 43);
        const startKey = localDateKey(startD);
        const events = await fetchHotFlashEventsBetween(userId, startKey, todayKey);
        const hfByDate: Record<string, number> = {};
        for (const ev of events) {
          if (ev.symptom_name !== "Hot flashes") continue;
          const dk = localDateKey(new Date(ev.occurred_at));
          hfByDate[dk] = (hfByDate[dk] || 0) + 1;
        }

        const out: BiometricInsight[] = [];

        // --- Skin temp ↔ Hot flashes (reuse the doctor-report helper) ---
        const corr = correlateSkinTempHotFlash(rows, hfByDate);
        if (corr.daysWithBoth >= 7 && corr.hotFlashDays > 0) {
          const pct = corr.pctOnElevatedDays ?? 0;
          const elevated = corr.elevatedHotFlashDays;
          const total = corr.hotFlashDays;
          const direction: Direction = pct >= 50 ? "harmful" : pct > 0 ? "mixed" : "unclear";
          // Effect-size analogue for the badge: count of matches.
          const showBadge = elevated > 0;
          out.push({
            key: "skintemp_hf",
            emoji: "🌡️",
            label: "Skin temperature",
            outcomeLabel: "Hot flashes",
            effectSize: showBadge ? elevated : 0,
            effectUnit: elevated === 1 ? "match" : "matches",
            effectDescription:
              elevated > 0
                ? `Your wearable detected an overnight temperature spike on ${elevated} of ${total} day${total === 1 ? "" : "s"} you logged a hot flash or night sweat (${pct}%).`
                : `Your wearable hasn't picked up overnight temperature spikes on the days you logged hot flashes in this window.`,
            daysAnalyzed: corr.daysWithBoth,
            confidence: confidenceFor(corr.daysWithBoth),
            direction,
            action:
              pct >= 50
                ? "Your device is picking up the same nights you're feeling."
                : "Keep logging — the pattern needs more nights to firm up.",
          });
        }

        // --- HRV ↔ Menopause Impact Score (higher HRV = better recovery) ---
        const hrvBuckets = bucketAgainstBaseline(rows, "hrv_ms", scoreByDate, "higher-better");
        const hrv = buildScoreInsight("hrv_score", "💗", "HRV", hrvBuckets);
        if (hrv) out.push(hrv);

        // --- Sleep efficiency ↔ Menopause Impact Score (higher quality = better) ---
        const sleepBuckets = bucketAgainstBaseline(rows, "sleep_quality_score", scoreByDate, "higher-better");
        const sleep = buildScoreInsight("sleepq_score", "😴", "Sleep efficiency", sleepBuckets);
        if (sleep) out.push(sleep);

        // --- Resting HR ↔ Menopause Impact Score (lower RHR = better) ---
        const rhrBuckets = bucketAgainstBaseline(rows, "resting_heart_rate", scoreByDate, "lower-better");
        const rhr = buildScoreInsight("rhr_score", "❤️", "Resting heart rate", rhrBuckets);
        if (rhr) out.push(rhr);

        // Sort by absolute effect, like TriggerInsightsCard.
        out.sort((a, b) => Math.abs(b.effectSize) - Math.abs(a.effectSize));
        setInsights(out);
      } catch (e) {
        console.warn("[BiometricInsights] failed", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  if (loading) return null;
  // Gate: render nothing when there's no wearable data at all.
  if (!hasWearable) return null;
  // Render nothing when no metric has enough overlapping days to qualify.
  if (insights.length === 0) return null;

  return (
    <div style={{ marginBottom: 14 }}>
      <style>{`@keyframes bic-fade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }`}</style>
      <div style={{ background: WH, border: "0.5px solid #E8D5DA", borderRadius: 18, padding: "16px 18px", marginBottom: 10 }}>
        <div style={{ fontSize: 10, color: RO, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 500, marginBottom: 6 }}>From your wearable</div>
        <div style={{ fontSize: 16, fontWeight: 500, color: GD, marginBottom: 10 }}>Biometric Insights — Last 30 Days</div>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 11, color: GR }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: AMBER }} /> Low
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: RO }} /> Building
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: TEAL }} /> Confident
          </span>
        </div>
      </div>
      <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr" }}>
        {insights.map((ins) => <Card key={ins.key} insight={ins} />)}
      </div>
    </div>
  );
}
// silence unused token lint
void TEAL_L; void AMBER_L;
